/**
 * Small presentational pieces shared by the Request Board's columns, cards and
 * item drawer: the person avatar, the type / status pills and the card itself.
 */
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AddPhotoAlternateOutlined from "@mui/icons-material/AddPhotoAlternateOutlined";
import ChatBubbleOutlineRounded from "@mui/icons-material/ChatBubbleOutlineRounded";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import ScheduleRounded from "@mui/icons-material/ScheduleRounded";
import ReplayRounded from "@mui/icons-material/ReplayRounded";
import UndoRounded from "@mui/icons-material/UndoRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { amber, slate, status } from "../../theme/colors";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import { linkTargetShort, linkTargetUrl } from "../../components/forms/FormPrimitives";
import { A } from "../admin/shared/adminTokens";
import {
  COL,
  NEUTRAL,
  personFor,
  displayTitle,
  isAuto,
  needByKey,
  ageLabel,
  columnByKey,
  tabsFor,
  platformShort,
  canHit,
  hitSummary,
} from "./requestBoardModel";

export const Avatar = ({ person, size = 22 }) => {
  const p = personFor(person);
  if (!p) return null;
  return (
    <Tooltip title={p.name} disableInteractive>
      <Flx
        center
        sx={{
          width: size,
          height: size,
          flexShrink: 0,
          borderRadius: "50%",
          bgcolor: p.color,
          color: "#FFFFFF",
          fontSize: size * 0.42,
          fontWeight: 700,
          letterSpacing: ".02em",
        }}
      >
        {p.initials}
      </Flx>
    </Tooltip>
  );
};

Avatar.propTypes = { person: PropTypes.string, size: PropTypes.number };

/** The person chip in a column header — avatar + name. */
export const PersonChip = ({ person }) => (
  <Flx
    ac
    g={0.75}
    sx={{
      pl: "3px",
      pr: 1.25,
      py: "3px",
      borderRadius: 99,
      border: `1px solid ${A.line}`,
      bgcolor: A.surface,
      flexShrink: 0,
    }}
  >
    <Avatar person={person} size={20} />
    <Txt sx={{ fontSize: 12, fontWeight: 600, color: A.ink2 }}>
      {personFor(person)?.name}
    </Txt>
  </Flx>
);

PersonChip.propTypes = { person: PropTypes.string };

export const Pill = ({ bg, fg, solid, children }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: 1,
      py: "2px",
      borderRadius: "6px",
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1.5,
      whiteSpace: "nowrap",
      bgcolor: solid ? fg : bg,
      color: solid ? "#FFFFFF" : fg,
    }}
  >
    {children}
  </Box>
);

Pill.propTypes = {
  bg: PropTypes.string,
  fg: PropTypes.string,
  solid: PropTypes.bool,
  children: PropTypes.node,
};

/** What a card needs: zero or more tags (Fix red, Change blue, Question amber). */
export const NeedTags = ({ needs }) =>
  (needs || []).map((key) => {
    const n = needByKey(key);
    return n ? (
      <Pill key={key} bg={n.bg} fg={n.fg}>
        {n.label}
      </Pill>
    ) : null;
  });

NeedTags.propTypes = { needs: PropTypes.array };

/** "Auto": the card was auto-generated (filed by a script, as `createdBy`). */
export const AutoPill = ({ item }) =>
  isAuto(item?.title) ? (
    <Tooltip
      disableInteractive
      title={`Auto-generated by ${personFor(item.createdBy)?.name || item.createdBy || "someone"}`}
    >
      <span>
        <Pill bg={slate[100]} fg={slate[600]}>
          Auto
        </Pill>
      </span>
    </Tooltip>
  ) : null;

AutoPill.propTypes = { item: PropTypes.object };

/**
 * A panel section: a chevron + uppercase title (click either to fold it), an
 * optional quiet `meta` after the title and `end` controls on the right, then
 * the content. No rule under the header.
 */
/**
 * A panel section: a hairline-ruled header (chevron + title + meta) that folds
 * its body. Sections stack flush; an open body leaves 24px before the next
 * header. `end` sits on the header's right while open; `endAlways` keeps it
 * there when folded too (e.g. Recurring Encounters' Log button).
 */
export const CollapsibleSection = ({ title, meta, end, endAlways = false, defaultOpen = true, children, sx }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={sx}>
      <Flx
        ac
        jb
        g={1}
        sx={{ minHeight: 28, mb: 1, pb: 1, borderBottom: `1px solid ${slate[100]}` }}
      >
        <Box
          component="button"
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            minWidth: 0,
            ml: "-4px",
            p: "2px 6px 2px 2px",
            border: 0,
            borderRadius: "6px",
            bgcolor: "transparent",
            font: "inherit",
            cursor: "pointer",
            "&:hover": { bgcolor: slate[100] },
            "&:focus-visible": { outline: `2px solid ${A.tealSoft2}` },
          }}
        >
          <ExpandMoreRounded
            sx={{
              fontSize: 18,
              color: A.muted,
              transition: "transform .15s",
              transform: open ? "none" : "rotate(-90deg)",
            }}
          />
          <Txt
            sx={{
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: ".08em",
              textTransform: "uppercase",
              color: A.muted,
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Txt>
          {meta ? (
            <Txt
              sx={{
                fontSize: 12,
                color: A.faint,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {meta}
            </Txt>
          ) : null}
        </Box>
        {open || endAlways ? end : null}
      </Flx>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pb: 3 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

CollapsibleSection.propTypes = {
  title: PropTypes.node,
  meta: PropTypes.node,
  end: PropTypes.node,
  endAlways: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  children: PropTypes.node,
  sx: PropTypes.object,
};

/** Urgent (solid red) / No rush (muted). Normal shows nothing. */
export const UrgencyPill = ({ urgency }) => {
  if (urgency === "urgent") {
    return (
      <Pill solid fg={status.error.fg}>
        Urgent
      </Pill>
    );
  }
  if (urgency === "no_rush") return <Pill bg={NEUTRAL.bg} fg={NEUTRAL.fg}>No rush</Pill>;
  return null;
};

UrgencyPill.propTypes = { urgency: PropTypes.string };

const RankBadge = ({ n }) => (
  <Flx
    center
    sx={{
      minWidth: 20,
      height: 20,
      px: "5px",
      borderRadius: "5px",
      border: `1px solid ${A.line}`,
      fontSize: 10.5,
      fontWeight: 700,
      color: A.muted,
      fontFamily: A.mono,
    }}
  >
    {n}
  </Flx>
);

RankBadge.propTypes = { n: PropTypes.number };

/** "Run into 14 times · 3 people · last Oct 20, 2:14 PM" — the card count's tooltip line. */
export const hitSentence = (summary) => {
  if (!summary.count) return "No encounters logged yet.";
  const people = summary.people.length;
  return [
    `Run into ${summary.count} time${summary.count === 1 ? "" : "s"}`,
    people > 1 ? `${people} people` : null,
    summary.lastAt ? `last ${formatDateTime(summary.lastAt, "datetime")}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
};

/**
 * Recurring Encounters on a card's footer: the encounter count, one click logs
 * one (repeat clicks count — that's the point). Teal once you've counted it
 * yourself. Not shown on Approved cards. Never opens or drags the card.
 */
export const HitButton = ({ item, me, onHit }) => {
  if (!canHit(item) || !onHit) return null;
  const s = hitSummary(item, me);
  const stop = (e) => e.stopPropagation();
  return (
    <Tooltip
      disableInteractive
      title={
        <>
          <strong>Recurring Encounters</strong> · click to log one
          <br />
          {hitSentence(s)}
          {s.mine ? ` · you: ${s.mine}` : ""}
        </>
      }
    >
      <Box
        component="button"
        type="button"
        draggable={false}
        aria-label={`Log a recurring encounter (${s.count} so far)`}
        onMouseDown={stop}
        onKeyDown={stop}
        onClick={(e) => {
          e.stopPropagation();
          onHit(item.id);
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          height: 22,
          px: "7px",
          border: `1px solid ${s.mine ? A.tealSoft2 : A.line}`,
          borderRadius: "999px",
          bgcolor: s.mine ? A.tealSoft : A.surface,
          color: s.mine ? A.tealDeep : A.muted,
          fontSize: 11.5,
          fontWeight: 600,
          lineHeight: "14px",
          fontFamily: "inherit",
          cursor: "pointer",
          transition: "background-color .15s, border-color .15s, color .15s",
          "& svg": { fontSize: 14, display: "block" },
          "&:hover": { borderColor: A.teal, color: A.tealDeep, bgcolor: A.tealSoft },
          "&:focus-visible": { outline: "none", boxShadow: `0 0 0 3px ${A.tealSoft2}` },
        }}
      >
        <ReplayRounded />
        <span>{s.count}</span>
      </Box>
    </Tooltip>
  );
};

HitButton.propTypes = {
  item: PropTypes.object.isRequired,
  me: PropTypes.string,
  onHit: PropTypes.func,
};

export const BoardCard = ({
  item,
  rank,
  nextUp,
  draggable,
  dragging,
  selected,
  onOpen,
  onDragStart,
  onDragEnd,
  me,
  onHit,
}) => {
  const col = columnByKey(item.column);
  return (
    <Box
      role="button"
      tabIndex={0}
      draggable={draggable}
      onClick={() => onOpen(item.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(item.id);
        }
      }}
      onDragStart={(e) => onDragStart(e, item)}
      onDragEnd={onDragEnd}
      data-card-id={item.id}
      sx={{
        position: "relative",
        bgcolor: A.surface,
        border: `1px solid ${A.line}`,
        borderRadius: "10px",
        p: 1.5,
        boxShadow: A.shadowSm,
        cursor: draggable ? "grab" : "pointer",
        opacity: dragging ? 0.4 : 1,
        outline: "none",
        transition: "box-shadow .15s, border-color .15s",
        "&:hover": { boxShadow: A.shadowMd, borderColor: A.lineStrong },
        "&:focus-visible": { boxShadow: `0 0 0 3px ${A.tealSoft2}` },
        // The card whose detail panel is open.
        ...(selected && {
          borderColor: A.teal,
          boxShadow: `0 0 0 3px ${A.tealSoft}`,
          "&:hover": { borderColor: A.teal, boxShadow: `0 0 0 3px ${A.tealSoft}` },
        }),
        // A card sent back from review keeps a left rule until it is resubmitted.
        ...(item.sentBack && {
          "&::before": {
            content: '""',
            position: "absolute",
            left: -1,
            top: 10,
            bottom: 10,
            width: 3,
            borderRadius: "0 3px 3px 0",
            bgcolor: status.warning.fg,
          },
        }),
      }}
    >
      <Flx ac g={0.75} wrap sx={{ mb: 1 }}>
        {/* In Requests a Queue card shows its rank; an Urgent card shows the
            Urgent label instead (and keeps it anywhere else on the board). */}
        {col?.ranked && item.urgency !== "urgent" && rank ? <RankBadge n={rank} /> : null}
        <UrgencyPill urgency={item.urgency} />
        {nextUp ? (
          <Pill bg={A.tealSoft} fg={A.tealDeep}>
            Next up
          </Pill>
        ) : null}
        <NeedTags needs={item.needs} />
        {/* Waiting on the backend: just what kind of change (the details are in the panel). */}
        {item.column === COL.BACKEND && item.blocker ? (
          <Pill bg={amber.light} fg={amber.chipText}>
            {item.blocker.label || "Backend change"}
          </Pill>
        ) : null}
        <AutoPill item={item} />
        {item.answer ? (
          <Pill bg={status.success.bg} fg={status.success.fg}>
            Answered
          </Pill>
        ) : null}
        {item.sentBack ? (
          <Pill bg={status.warning.bg} fg={status.warning.fg}>
            Sent back
          </Pill>
        ) : null}
        {item.returned ? (
          <Tooltip
            disableInteractive
            title={`Returned from ${columnByKey(item.returned.from)?.label} by ${personFor(item.returned.by)?.name || item.returned.by} · ${formatDateTime(item.returned.at, "datetime")}`}
          >
            {/* Icon-only: the tooltip says where it came back from. */}
            <Box
              component="span"
              aria-label={`Returned from ${columnByKey(item.returned.from)?.label}`}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 22,
                height: 20,
                borderRadius: "6px",
                bgcolor: status.missing.bg,
                color: status.missing.fg,
                "& svg": { fontSize: 14 },
              }}
            >
              <UndoRounded />
            </Box>
          </Tooltip>
        ) : null}
        {item.requesterEdit ? (
          <Tooltip
            disableInteractive
            title={`Edited by ${personFor(item.requesterEdit.by)?.name || "someone"} after development started: ${item.requesterEdit.fields.join(", ")} · ${formatDateTime(item.requesterEdit.at, "datetime")}`}
          >
            {/* Icon-only: the tooltip lists what changed. */}
            <Box
              component="span"
              aria-label={`Edited: ${item.requesterEdit.fields.join(", ")}`}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 22,
                height: 20,
                borderRadius: "6px",
                bgcolor: status.info.bg,
                color: status.info.fg,
                "& svg": { fontSize: 14 },
              }}
            >
              <EditOutlined />
            </Box>
          </Tooltip>
        ) : null}
        {item.release ? (
          <Pill bg={status.success.bg} fg={status.success.fg}>
            {item.release}
          </Pill>
        ) : null}
        {/* Which LOS it applies to, pinned to the right of the pill row. */}
        <Box sx={{ ml: "auto" }}>
          <Tooltip title="Company" disableInteractive>
            <span>
              <Pill bg={NEUTRAL.bg} fg={NEUTRAL.fg}>
                {platformShort(item.platform)}
              </Pill>
            </span>
          </Tooltip>
        </Box>
      </Flx>

      <Txt
        component="div"
        sx={{ fontSize: 13, fontWeight: 500, lineHeight: 1.4, color: A.ink }}
      >
        {displayTitle(item.title)}
      </Txt>
      {item.area ? (
        <Txt component="div" sx={{ mt: 0.5, fontSize: 11.5, color: A.muted }}>
          {item.area.replace(/\s*>\s*/g, " › ")}
        </Txt>
      ) : null}
      {/* The linked loan / screen: "#2713 · Fees", opens it in a new tab. */}
      {linkTargetUrl(item.context) && !item.context?.info?.unavailable ? (
        <Box
          component="a"
          href={linkTargetUrl(item.context)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          draggable={false}
          sx={{
            display: "inline-block",
            mt: 0.5,
            fontSize: 11.5,
            fontWeight: 600,
            color: A.tealDark,
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {linkTargetShort(item.context, tabsFor(item.context.loanType))}
        </Box>
      ) : item.context?.info?.unavailable ? (
        <Txt component="div" sx={{ mt: 0.5, fontSize: 11.5, color: A.faint }}>
          {linkTargetShort(item.context)} (unavailable)
        </Txt>
      ) : null}

      <Flx ac jb sx={{ mt: 1.25 }}>
        {/* Icon + count pairs: the svg is a block and the text has a 14px line box,
            so both centre on the same line. */}
        <Flx
          ac
          g={1.25}
          sx={{
            color: A.faint,
            "& svg": { fontSize: 14, display: "block" },
            "& .meta-n": { fontSize: 11.5, lineHeight: "14px", color: A.muted, display: "block" },
          }}
        >
          {item.comments.length ? (
            /* Comments stand out in the link teal: someone has said something. */
            <Flx ac g={0.5} sx={{ color: A.tealDark, "& .meta-n": { color: A.tealDark, fontWeight: 600 } }}>
              <ChatBubbleOutlineRounded />
              <Txt className="meta-n">{item.comments.length}</Txt>
            </Flx>
          ) : null}
          {imageCount(item) ? (
            <Flx ac g={0.5}>
              <ImageOutlined />
              <Txt className="meta-n">{imageCount(item)}</Txt>
            </Flx>
          ) : null}
          <Flx ac g={0.5}>
            <ScheduleRounded />
            <Txt className="meta-n">
              {ageLabel(item.createdDateTime)}
            </Txt>
          </Flx>
        </Flx>
        <Flx ac g={0.75}>
          <HitButton item={item} me={me} onHit={onHit} />
          <Avatar person={item.createdBy} />
        </Flx>
      </Flx>
    </Box>
  );
};

BoardCard.propTypes = {
  item: PropTypes.object.isRequired,
  rank: PropTypes.number,
  nextUp: PropTypes.bool,
  draggable: PropTypes.bool,
  dragging: PropTypes.bool,
  selected: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  onDragStart: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  me: PropTypes.string,
  onHit: PropTypes.func,
};

/* ─────────────────────────────── images ──────────────────────────────── */

/** Images on the item itself plus every image posted in its comments. */
export const imageCount = (item) =>
  (item.images?.length || 0) +
  (item.comments || []).reduce((n, c) => n + (c.images?.length || 0), 0);

/**
 * Image files on a paste event (a screenshot, or an image copied from a page or
 * a file). Screenshots arrive named "image.png", so they get a dated name.
 * Empty when the clipboard holds no image — the caller lets the paste through.
 */
export const pastedImageFiles = (e) => {
  const files = Array.from(e.clipboardData?.items || [])
    .filter((it) => it.kind === "file" && it.type.startsWith("image/"))
    .map((it) => it.getAsFile())
    .filter(Boolean);
  const stamp = new Date().toISOString().slice(0, 19).replace("T", " ").replace(/:/g, ".");
  return files.map((file, i) =>
    !file.name || /^image\.\w+$/i.test(file.name)
      ? new File([file], `Screenshot ${stamp}${i ? ` (${i + 1})` : ""}.${(file.type.split("/")[1] || "png").replace("jpeg", "jpg")}`, {
          type: file.type,
        })
      : file,
  );
};

/**
 * Turns picked / pasted / dropped image files into local attachments: an
 * object URL to preview, plus the File itself, which is uploaded
 * (POST /request_board/attachments) when the card / comment is saved.
 */
export const filesToImages = (fileList) =>
  Array.from(fileList || [])
    .filter((f) => f.type?.startsWith("image/"))
    .map((f, i) => ({
      id: `${Date.now()}-${i}-${f.name}`,
      name: f.name || "pasted-image.png",
      url: URL.createObjectURL(f),
      file: f,
    }));

/**
 * A row of thumbnails. Every thumbnail opens the viewer (a displayed file is
 * always previewable); `onRemove` adds a remove control for unsent drafts.
 */
/**
 * A row of image thumbnails. With `onAdd`, a dashed tile the same size as a
 * thumbnail ends the row and acts as the "add images" button (shown even when
 * there are no images yet).
 */
export const ImageThumbs = ({ images, onOpen, onRemove, onAdd, addLabel = "Add images", size = 72 }) =>
  images?.length || onAdd ? (
    <Flx g={1} wrap>
      {images.map((img, i) => (
        <Box key={img.id} sx={{ position: "relative" }}>
          <Tooltip title={img.name} disableInteractive>
            <Box
              component="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpen(images, i);
              }}
              aria-label={`Open ${img.name}`}
              sx={{
                display: "block",
                width: size,
                height: size,
                p: 0,
                border: `1px solid ${A.line}`,
                borderRadius: "8px",
                overflow: "hidden",
                cursor: "zoom-in",
                bgcolor: A.bg,
                "&:hover": { borderColor: A.tealSoft2, boxShadow: A.shadowMd },
                "&:focus-visible": { outline: `2px solid ${A.tealSoft2}` },
              }}
            >
              <Box
                component="img"
                src={img.url}
                alt={img.name}
                sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            </Box>
          </Tooltip>
          {onRemove ? (
            <IconButton
              size="small"
              aria-label={`Remove ${img.name}`}
              onClick={() => onRemove(img.id)}
              sx={{
                position: "absolute",
                top: -8,
                right: -8,
                width: 20,
                height: 20,
                bgcolor: A.surface,
                border: `1px solid ${A.line}`,
                "&:hover": { bgcolor: A.bg },
              }}
            >
              <CloseRounded sx={{ fontSize: 13 }} />
            </IconButton>
          ) : null}
        </Box>
      ))}
      {onAdd ? (
        <Tooltip title={addLabel} disableInteractive>
          <Box
            component="button"
            type="button"
            onClick={onAdd}
            aria-label={addLabel}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: size,
              height: size,
              p: 0,
              border: `1px dashed ${A.lineStrong}`,
              borderRadius: "8px",
              bgcolor: A.surface,
              color: A.muted,
              cursor: "pointer",
              transition: "border-color .15s, color .15s, background-color .15s",
              "&:hover": { borderColor: A.teal, color: A.tealDeep, bgcolor: A.tealSoft },
              "&:focus-visible": { outline: `2px solid ${A.tealSoft2}` },
            }}
          >
            <AddPhotoAlternateOutlined sx={{ fontSize: 22 }} />
          </Box>
        </Tooltip>
      ) : null}
    </Flx>
  ) : null;

ImageThumbs.propTypes = {
  images: PropTypes.array,
  onOpen: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  onAdd: PropTypes.func,
  addLabel: PropTypes.string,
  size: PropTypes.number,
};

/** Full-size viewer with previous / next for a set of images. */
export const ImageViewer = ({ viewer, onClose }) => {
  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(viewer?.index || 0), [viewer]);
  const images = viewer?.images || [];
  const img = images[index];
  const step = (d) => setIndex((i) => (i + d + images.length) % images.length);

  return (
    <Dialog
      open={!!img}
      onClose={onClose}
      maxWidth="lg"
      // Above the item drawer, which is itself a modal.
      sx={{ zIndex: 1400 }}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") step(-1);
        if (e.key === "ArrowRight") step(1);
      }}
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
    >
      {img ? (
        <Box>
          <Flx ac jb g={1} sx={{ px: 2, py: 1, borderBottom: `1px solid ${A.line}` }}>
            <Txt sx={{ fontSize: 13, fontWeight: 600, color: A.ink }}>
              {img.name}
              {images.length > 1 ? (
                <Box component="span" sx={{ color: A.faint, fontWeight: 500 }}>
                  {`  ·  ${index + 1} of ${images.length}`}
                </Box>
              ) : null}
            </Txt>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <CloseRounded sx={{ fontSize: 18 }} />
            </IconButton>
          </Flx>
          <Flx center sx={{ position: "relative", bgcolor: A.bg, p: 2 }}>
            <Box
              component="img"
              src={img.url}
              alt={img.name}
              sx={{ maxWidth: "min(1100px, 86vw)", maxHeight: "76vh", display: "block" }}
            />
            {images.length > 1 ? (
              <>
                <IconButton
                  onClick={() => step(-1)}
                  aria-label="Previous image"
                  sx={{ position: "absolute", left: 8, bgcolor: A.surface, boxShadow: A.shadowMd }}
                >
                  <ChevronLeftRounded />
                </IconButton>
                <IconButton
                  onClick={() => step(1)}
                  aria-label="Next image"
                  sx={{ position: "absolute", right: 8, bgcolor: A.surface, boxShadow: A.shadowMd }}
                >
                  <ChevronRightRounded />
                </IconButton>
              </>
            ) : null}
          </Flx>
        </Box>
      ) : null}
    </Dialog>
  );
};

ImageViewer.propTypes = {
  viewer: PropTypes.shape({ images: PropTypes.array, index: PropTypes.number }),
  onClose: PropTypes.func.isRequired,
};
